/**
 * Grid Calculation Engine
 *
 * Computes MGRS/UTM grid line positions for a visible map viewport.
 * Handles grid interval selection, UTM zone detection, coordinate
 * snapping, and grid line generation with proper labeling.
 *
 * Pure JS — no React, no DOM dependencies.
 */

import { latLngToUtm } from './mgrsConvert.js';
import { toPoint } from 'mgrs';

/**
 * Return grid spacing in meters based on the current map zoom level.
 *
 * @param {number} zoomLevel - Leaflet/Mapbox zoom level (1-20+)
 * @returns {number} Grid spacing in meters
 */
export function getGridInterval(zoomLevel) {
  if (zoomLevel >= 15) return 100;
  if (zoomLevel >= 12) return 1000;
  if (zoomLevel >= 9) return 10000;
  return 100000; // zoom <= 8 (and fallback for very low zoom)
}

/**
 * Snap a UTM easting or northing value down to the nearest grid interval.
 *
 * @param {number} utmValue - UTM easting or northing in meters
 * @param {number} interval - Grid interval in meters
 * @returns {number} Snapped value
 */
export function snapToGrid(utmValue, interval) {
  return Math.floor(utmValue / interval) * interval;
}

/**
 * Determine which UTM zones are visible in the given viewport bounds.
 *
 * UTM zones are 6 degrees wide. Zone 1 starts at 180W.
 * Zone number = floor((lng + 180) / 6) + 1, clamped to [1, 60].
 *
 * @param {{ north: number, south: number, east: number, west: number }} bounds
 * @returns {number[]} Sorted array of UTM zone numbers
 */
export function getUtmZonesInView(bounds) {
  const { east, west } = bounds;

  // Handle the dateline crossing case
  if (west > east) {
    // Viewport wraps around the dateline
    const zonesWest = getZonesInRange(west, 180);
    const zonesEast = getZonesInRange(-180, east);
    const combined = new Set([...zonesWest, ...zonesEast]);
    return [...combined].sort((a, b) => a - b);
  }

  return getZonesInRange(west, east);
}

/**
 * Compute grid lines for the visible map viewport.
 *
 * Workflow:
 *   1. Determine the grid interval from zoom level
 *   2. Find UTM zones in view
 *   3. For each zone, convert bounds to UTM, snap to grid, generate lines
 *   4. Convert line endpoints back to lat/lng
 *
 * @param {{ north: number, south: number, east: number, west: number }} bounds
 * @param {number} zoomLevel
 * @returns {Array<{
 *   start: { lat: number, lng: number },
 *   end: { lat: number, lng: number },
 *   label: string,
 *   orientation: 'easting' | 'northing'
 * }>}
 */
export function computeGridLines(bounds, zoomLevel) {
  const interval = getGridInterval(zoomLevel);
  const zones = getUtmZonesInView(bounds);
  const lines = [];

  for (const zone of zones) {
    const zoneLines = computeGridLinesForZone(bounds, zone, interval);
    lines.push(...zoneLines);
  }

  return lines;
}

// ─── Internal helpers ──────────────────────────────────────────────

/**
 * Get UTM zone numbers for a longitude range.
 *
 * @param {number} westLng - Western longitude
 * @param {number} eastLng - Eastern longitude
 * @returns {number[]} Sorted array of zone numbers
 */
function getZonesInRange(westLng, eastLng) {
  const zones = new Set();
  const zoneWest = lngToZone(westLng);
  const zoneEast = lngToZone(eastLng);

  for (let z = zoneWest; z <= zoneEast; z++) {
    zones.add(z);
  }

  return [...zones].sort((a, b) => a - b);
}

/**
 * Convert longitude to UTM zone number.
 *
 * @param {number} lng - Longitude in decimal degrees
 * @returns {number} UTM zone number (1-60)
 */
function lngToZone(lng) {
  // Clamp longitude to [-180, 180)
  let normalizedLng = lng;
  while (normalizedLng < -180) normalizedLng += 360;
  while (normalizedLng >= 180) normalizedLng -= 360;

  const zone = Math.floor((normalizedLng + 180) / 6) + 1;
  return Math.max(1, Math.min(60, zone));
}

/**
 * Get the longitude boundaries of a UTM zone.
 *
 * @param {number} zone - UTM zone number (1-60)
 * @returns {{ west: number, east: number }}
 */
function getZoneBounds(zone) {
  const west = (zone - 1) * 6 - 180;
  const east = west + 6;
  return { west, east };
}

/**
 * Compute grid lines for a single UTM zone within the viewport.
 *
 * @param {{ north: number, south: number, east: number, west: number }} bounds
 * @param {number} zone - UTM zone number
 * @param {number} interval - Grid spacing in meters
 * @returns {Array} Grid line objects
 */
function computeGridLinesForZone(bounds, zone, interval) {
  const lines = [];
  const zoneBounds = getZoneBounds(zone);

  // Clip the viewport to this zone's longitude range
  const clippedWest = Math.max(bounds.west, zoneBounds.west);
  const clippedEast = Math.min(bounds.east, zoneBounds.east);
  const clippedNorth = Math.min(bounds.north, 84); // MGRS limit
  const clippedSouth = Math.max(bounds.south, -80); // MGRS limit

  if (clippedWest >= clippedEast || clippedSouth >= clippedNorth) {
    return lines;
  }

  // Get UTM coordinates for the four corners of the clipped viewport
  const swUtm = safeLatLngToUtm(clippedSouth, clippedWest, zone);
  const neUtm = safeLatLngToUtm(clippedNorth, clippedEast, zone);
  const nwUtm = safeLatLngToUtm(clippedNorth, clippedWest, zone);
  const seUtm = safeLatLngToUtm(clippedSouth, clippedEast, zone);

  if (!swUtm || !neUtm || !nwUtm || !seUtm) {
    return lines;
  }

  // Find the overall easting and northing ranges
  const minEasting = Math.min(swUtm.easting, nwUtm.easting);
  const maxEasting = Math.max(seUtm.easting, neUtm.easting);
  const minNorthing = Math.min(swUtm.northing, seUtm.northing);
  const maxNorthing = Math.max(nwUtm.northing, neUtm.northing);

  // Snap to grid boundaries (extend outward)
  const startEasting = snapToGrid(minEasting, interval);
  const endEasting = snapToGrid(maxEasting, interval) + interval;
  const startNorthing = snapToGrid(minNorthing, interval);
  const endNorthing = snapToGrid(maxNorthing, interval) + interval;

  // Safety: cap the number of lines to prevent runaway loops
  const maxLines = 200;

  // Generate easting lines (vertical lines — constant easting, varying northing)
  let lineCount = 0;
  for (let e = startEasting; e <= endEasting && lineCount < maxLines; e += interval) {
    const startPt = utmToLatLng(zone, e, startNorthing, clippedSouth >= 0);
    const endPt = utmToLatLng(zone, e, endNorthing, clippedNorth >= 0);

    if (startPt && endPt) {
      lines.push({
        start: startPt,
        end: endPt,
        label: formatGridLabel(e, interval),
        orientation: 'easting',
      });
      lineCount++;
    }
  }

  // Generate northing lines (horizontal lines — constant northing, varying easting)
  lineCount = 0;
  for (let n = startNorthing; n <= endNorthing && lineCount < maxLines; n += interval) {
    const startPt = utmToLatLng(zone, startEasting, n, clippedSouth >= 0);
    const endPt = utmToLatLng(zone, endEasting, n, clippedSouth >= 0);

    if (startPt && endPt) {
      lines.push({
        start: startPt,
        end: endPt,
        label: formatGridLabel(n, interval),
        orientation: 'northing',
      });
      lineCount++;
    }
  }

  return lines;
}

/**
 * Convert UTM coordinates to lat/lng using the mgrs library as an intermediary.
 *
 * Constructs a synthetic MGRS string from UTM components, then converts to lat/lng.
 *
 * @param {number} zone - UTM zone number
 * @param {number} easting - UTM easting in meters
 * @param {number} northing - UTM northing in meters
 * @param {boolean} northern - true if northern hemisphere
 * @returns {{ lat: number, lng: number } | null}
 */
function utmToLatLng(zone, easting, northing, northern) {
  try {
    // Use a simple equirectangular approximation to get a lat for band letter lookup
    // Then use the mgrs library for accurate conversion
    const approxLat = northern
      ? (northing / 111320)
      : ((northing - 10000000) / 111320);

    const bandLetter = getUtmBandLetter(approxLat);
    const squareId = getSquareId(zone, easting, northing);

    // Build the MGRS string: zone + band + square + easting(5) + northing(5)
    const eastingInSquare = Math.round(easting % 100000);
    const northingInSquare = Math.round(northing % 100000);

    const mgrsStr = `${zone}${bandLetter}${squareId}` +
      `${String(eastingInSquare).padStart(5, '0')}` +
      `${String(northingInSquare).padStart(5, '0')}`;

    const point = toPoint(mgrsStr);
    if (point && point.length >= 2) {
      return { lat: point[1], lng: point[0] };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Safely get UTM coordinates for a lat/lng, catching errors near zone edges.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {number} expectedZone
 * @returns {{ easting: number, northing: number } | null}
 */
function safeLatLngToUtm(lat, lng, expectedZone) {
  try {
    const utm = latLngToUtm(lat, lng);
    // If the point falls in a different zone, project it into the expected zone
    // by using the zone's central meridian
    return { easting: utm.easting, northing: utm.northing };
  } catch {
    return null;
  }
}

/**
 * Get the UTM latitude band letter for a given latitude.
 *
 * @param {number} lat - Latitude in decimal degrees
 * @returns {string} Band letter (C through X, excluding I and O)
 */
function getUtmBandLetter(lat) {
  const bands = 'CDEFGHJKLMNPQRSTUVWX';
  if (lat < -80) return 'C';
  if (lat >= 84) return 'X';
  const index = Math.floor((lat + 80) / 8);
  return bands[Math.min(index, bands.length - 1)];
}

/**
 * Compute the MGRS 100km square ID from UTM zone, easting, and northing.
 *
 * @param {number} zone - UTM zone number
 * @param {number} easting - UTM easting in meters
 * @param {number} northing - UTM northing in meters
 * @returns {string} Two-character square ID
 */
function getSquareId(zone, easting, northing) {
  // Column letter (from easting)
  // The column letter set repeats every 3 zones
  const colSets = ['ABCDEFGH', 'JKLMNPQR', 'STUVWXYZ'];
  const setIndex = (zone - 1) % 3;
  const col100km = Math.floor(easting / 100000);
  // col100km should be 1-8; clamp it
  const colIdx = Math.max(0, Math.min(7, col100km - 1));
  const colLetter = colSets[setIndex][colIdx];

  // Row letter (from northing)
  // The row letter set alternates odd/even zones
  const rowSets = [
    'ABCDEFGHJKLMNPQRSTUV', // odd zones (1, 3, 5, ...)
    'FGHJKLMNPQRSTUVABCDE', // even zones (2, 4, 6, ...)
  ];
  const rowSet = rowSets[zone % 2 === 0 ? 1 : 0];
  const row100km = Math.floor(northing / 100000) % 20;
  const rowLetter = rowSet[row100km];

  return `${colLetter}${rowLetter}`;
}

/**
 * Format a grid line label from a UTM value and interval.
 *
 * Shows the significant digits of the easting or northing.
 *
 * @param {number} value - UTM value in meters
 * @param {number} interval - Grid interval in meters
 * @returns {string}
 */
function formatGridLabel(value, interval) {
  // Show the last significant digits based on interval
  // 100km grid: show the 100km digit (e.g., "4" for 400000)
  // 10km grid: show two digits (e.g., "49" for 490000)
  // 1km grid: show three digits (e.g., "498" for 498000)
  // 100m grid: show four digits (e.g., "4983" for 498300)
  if (interval >= 100000) {
    return String(Math.floor(value / 100000));
  }
  if (interval >= 10000) {
    return String(Math.floor(value / 10000) % 100).padStart(2, '0');
  }
  if (interval >= 1000) {
    return String(Math.floor(value / 1000) % 100).padStart(2, '0');
  }
  return String(Math.floor(value / 100) % 1000).padStart(3, '0');
}
